import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Navbar from "../../components/Navbar/Navbar";
import "./GChat.css";

const PRESETS = [
  { label: "Last 1 Week", days: 7 },
  { label: "Last 2 Weeks", days: 14 },
  { label: "Last 1 Month", days: 30 },
  { label: "Last 3 Months", days: 90 },
];

const formatDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const toInputDate = (d) => d.toISOString().split("T")[0];

const GChat = () => {
  const today = toInputDate(new Date());
  const [startDate, setStartDate] = useState(
    toInputDate(new Date(Date.now() - 7 * 86400000))
  );
  const [endDate, setEndDate] = useState(today);

  const [spaces, setSpaces] = useState([]);
  const [newSpaceName, setNewSpaceName] = useState("");
  const [spaceError, setSpaceError] = useState("");

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [expandedJob, setExpandedJob] = useState(null);

  // --- Space Management ---
  const fetchSpaces = useCallback(async () => {
    try {
      const res = await axios.get("/gchat/spaces");
      setSpaces(res.data);
    } catch (err) {
      console.error("Failed to fetch spaces:", err);
    }
  }, []);

  const handleAddSpace = async () => {
    setSpaceError("");
    if (!newSpaceName.trim()) {
      setSpaceError("Please enter a space name.");
      return;
    }
    try {
      await axios.post("/gchat/spaces", { display_name: newSpaceName.trim() });
      setNewSpaceName("");
      await fetchSpaces();
    } catch (err) {
      setSpaceError(err.response?.data?.error || "Failed to add space.");
    }
  };

  const handleRemoveSpace = async (id) => {
    try {
      await axios.delete(`/gchat/spaces/${id}`);
      await fetchSpaces();
    } catch (err) {
      console.error("Failed to remove space:", err);
    }
  };

  // --- Job Management ---
  const fetchJobs = useCallback(async () => {
    try {
      const res = await axios.get("/gchat/jobs");
      setJobs(res.data);
    } catch (err) {
      console.error("Failed to fetch jobs:", err);
    }
  }, []);

  const handlePreset = (days) => {
    const end = new Date();
    const start = new Date(Date.now() - days * 86400000);
    setStartDate(toInputDate(start));
    setEndDate(toInputDate(end));
  };

  const handleRunJob = async () => {
    setError("");
    setSuccess("");
    if (!startDate || !endDate) {
      setError("Please select both start and end dates.");
      return;
    }
    if (startDate > endDate) {
      setError("Start date must be before end date.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post("/gchat/run", {
        start_date: startDate,
        end_date: endDate,
      });
      setSuccess(`Job ${res.data.job_id} started successfully.`);
      await fetchJobs();
    } catch (err) {
      setError(
        err.response?.data?.error || "Failed to start job. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (!window.confirm(`Delete job ${jobId} history entry?`)) {
      return;
    }
    try {
      await axios.delete(`/gchat/jobs/${jobId}`);
      await fetchJobs();
    } catch (err) {
      alert(err.response?.data?.error || "Failed to delete job.");
    }
  };

  // On mount
  useEffect(() => {
    fetchSpaces();
    fetchJobs();
  }, [fetchSpaces, fetchJobs]);

  // Poll active jobs
  useEffect(() => {
    const hasActive = jobs.some(
      (j) => j.status === "running" || j.status === "pending"
    );
    if (!hasActive) return;
    const interval = setInterval(fetchJobs, 3000);
    return () => clearInterval(interval);
  }, [jobs, fetchJobs]);

  const isRunning = jobs.some(
    (j) => j.status === "running" || j.status === "pending"
  );

  return (
    <div>
      <Navbar />
      <div className="gchat-container">
        <div className="gchat-header">
          <h1>Google Chat Analysis</h1>
          <p>
            Configure Google Chat spaces and run analysis jobs.
          </p>
        </div>

        {/* Space Configuration */}
        <div className="gchat-card">
          <h2>Configured Spaces</h2>
          <div className="space-config-form">
            <input
              type="text"
              placeholder="Enter Google Chat space name..."
              value={newSpaceName}
              onChange={(e) => setNewSpaceName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAddSpace()}
            />
            <button className="add-space-btn" onClick={handleAddSpace}>
              Add Space
            </button>
          </div>
          {spaceError && <p className="gchat-error-msg">{spaceError}</p>}
          {spaces.length === 0 ? (
            <p className="no-spaces">
              No spaces configured yet. Add a Google Chat space name above.
            </p>
          ) : (
            <div className="space-list">
              {spaces.map((space) => (
                <div className="space-chip" key={space.id}>
                  <span>{space.display_name}</span>
                  <button
                    className="remove-space-btn"
                    onClick={() => handleRemoveSpace(space.id)}
                    title="Remove space"
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Run Job */}
        <div className="gchat-card">
          <h2>Run Analysis Job</h2>
          <div className="gchat-date-form">
            <div className="gchat-date-field">
              <label>Start Date</label>
              <input
                type="date"
                value={startDate}
                max={today}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="gchat-date-field">
              <label>End Date</label>
              <input
                type="date"
                value={endDate}
                max={today}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            <button
              className="gchat-run-btn"
              onClick={handleRunJob}
              disabled={loading || isRunning}
            >
              {loading
                ? "Starting..."
                : isRunning
                ? "Job Running..."
                : "Run Job"}
            </button>
          </div>
          <div className="gchat-presets">
            {PRESETS.map((p) => (
              <button
                key={p.days}
                className="gchat-preset-btn"
                onClick={() => handlePreset(p.days)}
              >
                {p.label}
              </button>
            ))}
          </div>
          {error && <p className="gchat-error-msg">{error}</p>}
          {success && <p className="gchat-success-msg">{success}</p>}
        </div>

        {/* Job History */}
        <div className="gchat-card">
          <h2>Job History</h2>
          {jobs.length === 0 ? (
            <p className="gchat-no-jobs">
              No jobs yet. Configure spaces and run your first analysis job.
            </p>
          ) : (
            <table className="gchat-jobs-table">
              <thead>
                <tr>
                  <th>Job ID</th>
                  <th>Date Range</th>
                  <th>Status</th>
                  <th>Progress</th>
                  <th>Threads</th>
                  <th>Started</th>
                  <th>Completed</th>
                  <th>Logs</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => (
                  <React.Fragment key={job.id}>
                    <tr>
                      <td style={{ fontFamily: "monospace", fontWeight: 600 }}>
                        {job.id}
                      </td>
                      <td>
                        {job.start_date} → {job.end_date}
                      </td>
                      <td>
                        <span
                          className={`gchat-status-badge gchat-status-${job.status}`}
                        >
                          {job.status}
                        </span>
                      </td>
                      <td style={{ minWidth: 120 }}>
                        <div className="gchat-progress-bar-container">
                          <div
                            className={`gchat-progress-bar-fill ${job.status}`}
                            style={{ width: `${job.progress}%` }}
                          />
                        </div>
                        <span className="gchat-progress-text">
                          {job.progress}%
                        </span>
                      </td>
                      <td>
                        <span title="Processed">
                          {job.processed_threads}
                        </span>
                        {job.skipped_threads > 0 && (
                          <span
                            title="Skipped (already analysed)"
                            style={{ color: "#a0aec0" }}
                          >
                            {" "}
                            +{job.skipped_threads}s
                          </span>
                        )}
                        {job.error_threads > 0 && (
                          <span
                            title="Errors"
                            style={{ color: "#e53e3e" }}
                          >
                            {" "}
                            / {job.error_threads}err
                          </span>
                        )}
                        <span style={{ color: "#718096" }}>
                          {" "}
                          / {job.total_threads}
                        </span>
                      </td>
                      <td>{formatDate(job.started_at)}</td>
                      <td>{formatDate(job.completed_at)}</td>
                      <td>
                        <button
                          className="gchat-log-toggle-btn"
                          onClick={() =>
                            setExpandedJob(
                              expandedJob === job.id ? null : job.id
                            )
                          }
                        >
                          {expandedJob === job.id ? "Hide" : "View"}
                        </button>
                      </td>
                      <td>
                        <button
                          className="gchat-delete-btn"
                          onClick={() => handleDeleteJob(job.id)}
                          disabled={
                            job.status === "running" ||
                            job.status === "pending"
                          }
                          title={
                            job.status === "running" || job.status === "pending"
                              ? "Cannot delete while job is active"
                              : "Delete job history entry"
                          }
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                    {expandedJob === job.id && (
                      <tr>
                        <td colSpan="9" style={{ padding: 0 }}>
                          <div className="gchat-log-panel">
                            {job.logs && job.logs.length > 0 ? (
                              job.logs.map((log, i) => <p key={i}>{log}</p>)
                            ) : (
                              <p>No logs yet.</p>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default GChat;
